import { useCallback, useRef, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Text } from "../../../../components/common/typography";
import LandingPage from "../../../landing/presentation/pages/landing-page";
import { HandpickedDealsSection } from "../../../featured-deals/presentation/components/handpicked-deals-section";
import { useChat } from "../../hooks/useChat";
import { useAiSearchSessionKey } from "../../hooks/useAiSearchSessionKey";

const STAR_PATH = "M20,0 L21.5,18.5 L40,20 L21.5,21.5 L20,40 L18.5,21.5 L0,20 L18.5,18.5Z";

function SparkleIcon({ size = 14, white }: { size?: number; white?: boolean }) {
  return (
    <svg
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      style={{ fill: white ? "#ffffff" : "var(--color-accent-cyan-700)" }}
      aria-hidden
    >
      <path d={STAR_PATH} />
    </svg>
  );
}

export function NoticeBanner() {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-accentCyan bg-white/70 px-5 py-4 backdrop-blur-sm">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentCyan">
        <SparkleIcon size={18} white />
      </div>
      <Text variant="bodySm" tone="body">
        You will be able to see the full product comparison once your account is approved.
      </Text>
    </div>
  );
}


/**
 * Route entry. Keyed on the navigation session so that "Home", the header
 * search and every other push/pop onto /ai-search start from a clean
 * assistant instead of inheriting the still-mounted conversation — see
 * `useAiSearchSessionKey`.
 */
export function AiSearchPage() {
  const sessionKey = useAiSearchSessionKey();
  return <AiSearchSession key={sessionKey} />;
}

function AiSearchSession() {
  const [searchParams, setSearchParams] = useSearchParams();
  // `?q=` (header "Ask Procurtal") is consumed once per session: captured on
  // mount and handed to the landing page to submit, then dropped from the
  // URL so a refresh doesn't re-run the query.
  const [initialQuery] = useState(() => searchParams.get("q")?.trim() ?? "");
  const chatIdParam = searchParams.get("chatId") ?? "";
  const resultsParamOpen = searchParams.get("results") === "1" || !!chatIdParam;
  const loadedChatRef = useRef<string>("");

  const { messages, responding, activeChatId, sendMessage, loadChat, retryLastMessage, stopGenerating } = useChat();

  // Preload an existing chat when the page is opened with `?chatId=…` (e.g.
  // from My Conversations, or a refresh of an active conversation). We keep
  // chatId in the URL so a refresh re-loads the same chat instead of
  // dropping back to an empty assistant. The loadedChatRef guard stops the
  // effect from re-firing when the URL is rewritten in-place.
  useEffect(() => {
    if (!chatIdParam) return;
    if (loadedChatRef.current === chatIdParam) return;
    loadedChatRef.current = chatIdParam;
    void loadChat(chatIdParam);
    if (!searchParams.get("results")) {
      const next = new URLSearchParams(searchParams);
      next.set("results", "1");
      setSearchParams(next, { replace: true });
    }
  }, [chatIdParam, loadChat, searchParams, setSearchParams]);

  // Once the backend assigns a chatId for a freshly-started conversation,
  // push it into the URL (replace, not push — we don't want a history entry
  // per message). This is what makes refresh restore the chat.
  useEffect(() => {
    if (!activeChatId) return;
    if (searchParams.get("chatId") === activeChatId) return;
    loadedChatRef.current = activeChatId;
    const next = new URLSearchParams(searchParams);
    next.set("chatId", activeChatId);
    next.set("results", "1");
    setSearchParams(next, { replace: true });
  }, [activeChatId, searchParams, setSearchParams]);

  const setResultsRouteState = useCallback(
    (open: boolean) => {
      const next = new URLSearchParams(searchParams);
      if (open) {
        next.set("results", "1");
      } else {
        next.delete("results");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSearch = useCallback((query: string) => {
    void sendMessage(query);
  }, [sendMessage]);

  // Widget callback signature carries an optional productId (e.g. "Chat
  // about <product>") that doesn't line up positionally with sendMessage's
  // own (text, attachments?, productId?) signature.
  const handleWidgetSendMessage = useCallback((text: string, productId?: number) => {
    void sendMessage(text, undefined, productId);
  }, [sendMessage]);

  useEffect(() => {
    if (!searchParams.has("q")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <LandingPage
      embedded
      afterHero={<HandpickedDealsSection />}
      initialQuery={initialQuery}
      restoreResultsOpen={resultsParamOpen}
      onResultsOpenChange={setResultsRouteState}
      onSearch={handleSearch}
      chatTranscript={messages}
      chatResponding={responding}
      chatRetry={retryLastMessage}
      chatStopGenerating={stopGenerating}
      chatSendMessage={handleWidgetSendMessage}
    />
  );
}

export default AiSearchPage;
